// ============================================================
// TILT — HUD
// ------------------------------------------------------------
// The chrome. Everything OUTSIDE the glass.
// UI palette ONLY — never a PF.* color in here (theme.js LAW 1).
//
// The HUD is a pure function of the snapshot. It holds no game
// state. If you find yourself wanting to store something here,
// it belongs in the engine.
// ============================================================

import { UI, PANEL, PANEL_LIT, RECESS, BRASS_METAL, facet, facetTB,
         RAISED, SUNK, bloom, glow, SET } from "./theme";
import { getPower, CHARGE_MAX } from "./Powers";
import { BOSS_MOVES, GUARDIAN_MOVES } from "./BossKit";
import { tintOf } from "./theme";

// ── Score readout ─────────────────────────────────────────────
export function ScoreBoard({ snap, label = "SCORE", accent = UI.cool }) {
  return (
    <div style={{
      background: PANEL, clipPath: facet(8), padding: "10px 14px",
      boxShadow: RAISED, minWidth: 150,
    }}>
      <div style={{
        fontFamily: UI.font, fontSize: 9, letterSpacing: 3,
        color: UI.textFaint, marginBottom: 2,
      }}>{label}</div>
      <div style={{
        fontFamily: UI.fontNum, fontSize: 26, fontWeight: 700,
        color: accent, textShadow: glow(accent), lineHeight: 1,
      }}>
        {snap.score.toLocaleString()}
      </div>
      {snap.combo > 1 && (
        <div style={{
          fontFamily: UI.font, fontSize: 10, letterSpacing: 1,
          color: UI.brassBright, marginTop: 3,
          animation: "tilt-pulse 0.7s ease-in-out infinite",
        }}>
          ×{snap.combo} COMBO
          {snap.multiplier > 1 && ` · ${snap.multiplier}× MULT`}
        </div>
      )}
    </div>
  );
}

// ── Charge meter — the power economy, made visible ────────────
export function ChargeMeter({ snap }) {
  const pct = (snap.charge / (snap.chargeMax || CHARGE_MAX)) * 100;
  const full = pct >= 99;
  return (
    <div style={{ flex: 1, minWidth: 120 }}>
      <div style={{
        display: "flex", justifyContent: "space-between",
        fontFamily: UI.font, fontSize: 9, letterSpacing: 2,
        color: UI.textFaint, marginBottom: 4,
      }}>
        <span>CHARGE</span>
        <span style={{ fontFamily: UI.fontNum, color: full ? UI.brassBright : UI.textDim }}>
          {Math.round(snap.charge)}
        </span>
      </div>
      <div style={{
        height: 10, background: RECESS, boxShadow: SUNK,
        clipPath: facetTB(4), position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0, width: `${pct}%`,
          background: full
            ? BRASS_METAL
            : `linear-gradient(90deg, ${UI.coolDim}, ${UI.cool})`,
          transition: "width 90ms linear",
          animation: full ? "tilt-charge 1.1s ease-in-out infinite" : "none",
        }} />
        {/* Sweep — the meter is alive */}
        <div style={{
          position: "absolute", top: 0, bottom: 0, width: "30%",
          background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.25),transparent)",
          animation: "tilt-sweep 2.4s linear infinite",
          pointerEvents: "none",
        }} />
      </div>
    </div>
  );
}

// ── Ball / tilt strip ─────────────────────────────────────────
export function StatusStrip({ snap }) {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
      <div>
        <div style={{ fontFamily: UI.font, fontSize: 9, letterSpacing: 2, color: UI.textFaint }}>
          BALL
        </div>
        <div style={{ display: "flex", gap: 3, marginTop: 3 }}>
          {Array.from({ length: Math.max(snap.ballsLeft, 0) }).map((_, i) => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: "50%",
              background: `radial-gradient(circle at 30% 30%, #fff, ${UI.chrome} 60%, ${UI.chromeDim})`,
              boxShadow: `0 0 4px ${UI.chrome}88`,
            }} />
          ))}
          {snap.ballsLeft <= 0 && (
            <span style={{ fontFamily: UI.font, fontSize: 10, color: UI.bad }}>—</span>
          )}
        </div>
      </div>

      <div>
        <div style={{ fontFamily: UI.font, fontSize: 9, letterSpacing: 2, color: UI.textFaint }}>
          TILT
        </div>
        <div style={{ display: "flex", gap: 3, marginTop: 3 }}>
          {Array.from({ length: snap.tiltMax }).map((_, i) => (
            <div key={i} style={{
              width: 12, height: 8,
              clipPath: facetTB(2),
              background: i < snap.tilt ? UI.hot : UI.panelEdge,
              boxShadow: i < snap.tilt ? bloom(UI.hot) : "none",
              animation: i < snap.tilt && snap.tilt >= snap.tiltMax - 1
                ? "tilt-danger 0.5s ease-in-out infinite" : "none",
            }} />
          ))}
        </div>
      </div>

      {snap.ballSave > 0 && (
        <div style={{
          fontFamily: UI.font, fontSize: 10, letterSpacing: 1,
          color: UI.good, textShadow: glow(UI.good),
          animation: "tilt-pulse 0.8s ease-in-out infinite",
        }}>
          ⛨ SAVE {snap.ballSave.toFixed(1)}s
        </div>
      )}

      {/* The boss silenced your flippers. The player MUST know why
          their inputs are dying — an unexplained dead flipper is
          indistinguishable from a broken game. */}
      {snap.flipperFracture > 0 && (
        <div style={{
          fontFamily: UI.font, fontSize: 10, letterSpacing: 1,
          color: UI.hot, textShadow: glow(UI.hot),
          animation: "tilt-danger 0.4s ease-in-out infinite",
        }}>
          ⛓ FRACTURED
        </div>
      )}
    </div>
  );
}

// ── Power bar — the loadout, live ─────────────────────────────
// `tile` is the ONE size number. Everything inside a tile — the
// glyph, the name, the cost, the facet — is derived from it, so a
// 62px duel console and a 92px solo console are the same component
// with one prop changed, and there is no second copy to drift.
// Default 62 = the duel console, untouched, byte for byte.
export function PowerBar({ snap, onCast, onAimStart, hotkeys = true, tile = 62 }) {
  const ids = [...snap.loadout, "nudge"];
  const gap = Math.round(tile * 0.12);
  return (
    <div style={{ display: "flex", gap, flexWrap: "wrap" }}>
      {ids.map((id, i) => {
        const p = getPower(id);
        if (!p) return null;
        const tint = tintOf(p.tint);
        const cd   = snap.cooldowns[id] || 0;
        const afford = snap.charge >= p.cost && cd <= 0 && snap.phase === "live";
        const arming = snap.aiming === id;

        return (
          <button
            key={id}
            onClick={() => {
              if (!afford) return;
              if (p.target === "point") onAimStart(id);
              else onCast(id);
            }}
            disabled={!afford}
            title={`${p.name} — ${p.blurb}`}
            style={{
              position: "relative",
              width: tile, height: tile,
              clipPath: facet(Math.round(tile * 0.13)),
              background: arming ? PANEL_LIT : PANEL,
              border: "none",
              cursor: afford ? "pointer" : "not-allowed",
              opacity: afford ? 1 : 0.38,
              boxShadow: arming ? bloom(tint) : RAISED,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              gap: Math.round(tile * 0.03),
              transition: "opacity 120ms, box-shadow 120ms",
              padding: 0,
            }}
          >
            <span style={{
              fontSize: Math.round(tile * 0.32), color: tint,
              textShadow: afford ? glow(tint) : "none",
              animation: arming ? "tilt-hum 0.8s ease-in-out infinite" : "none",
              lineHeight: 1,
            }}>{p.glyph}</span>
            <span style={{
              fontFamily: UI.font, fontSize: Math.max(8, Math.round(tile * 0.115)),
              letterSpacing: 1,
              color: UI.textDim, textShadow: SET,
            }}>{p.name.toUpperCase()}</span>
            <span style={{
              fontFamily: UI.fontNum, fontSize: Math.max(9, Math.round(tile * 0.135)),
              color: p.cost === 0 ? UI.good : (snap.charge >= p.cost ? UI.brassBright : UI.textFaint),
            }}>
              {p.cost === 0 ? "FREE" : p.cost}
            </span>
            {/* The badge names the key that ACTUALLY casts it. The
                loadout is 1–4; nudge is N. It used to say "5", and
                nothing on this planet was bound to 5. */}
            {hotkeys && (
              <span style={{
                position: "absolute", top: Math.round(tile * 0.05), right: Math.round(tile * 0.09),
                fontFamily: UI.fontNum, fontSize: Math.max(8, Math.round(tile * 0.12)),
                color: UI.textFaint,
              }}>{i < snap.loadout.length ? i + 1 : "N"}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Sabotage bar — the duel-only kit, aimed at THEM ───────────
// Visually HOT (magenta) where the self-kit is COOL (cyan). The
// player must never be able to confuse "help me" with "hurt them"
// at a glance in the middle of a panic. Color IS the safety rail.
export function SabotageBar({ snap, discount = 1, onSabotage, onAimStart }) {
  const kit = snap.loadout.map(getPower).filter((p) => p?.sabotage);
  if (!kit.length) return null;

  return (
    <div>
      <div style={{
        fontFamily: UI.font, fontSize: 9, letterSpacing: 3,
        color: UI.hot, marginBottom: 6, textShadow: glow(UI.hot),
      }}>
        ☠ SABOTAGE {discount < 1 && (
          <span style={{ color: UI.brassBright }}>
            −{Math.round((1 - discount) * 100)}%
          </span>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {kit.map((p) => {
          const cost = Math.round(p.cost * discount);
          const afford = snap.charge >= cost && snap.phase === "live";
          const arming = snap.aiming === `sab:${p.id}`;
          return (
            <button
              key={p.id}
              onClick={() => {
                if (!afford) return;
                if (p.target === "point") onAimStart(`sab:${p.id}`);
                else onSabotage(p.id, null);
              }}
              disabled={!afford}
              title={`Cast ${p.name} at your opponent`}
              style={{
                width: 54, height: 54, clipPath: facet(7),
                background: arming
                  ? `linear-gradient(174deg, ${UI.hotDim}, ${UI.panel})`
                  : PANEL,
                border: "none",
                cursor: afford ? "crosshair" : "not-allowed",
                opacity: afford ? 1 : 0.35,
                boxShadow: arming ? bloom(UI.hot) : RAISED,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                padding: 0,
              }}
            >
              <span style={{
                fontSize: 17, color: UI.hot, textShadow: glow(UI.hot),
                animation: arming ? "tilt-hum 0.7s ease-in-out infinite" : "none",
              }}>{p.glyph}</span>
              <span style={{
                fontFamily: UI.font, fontSize: 7, letterSpacing: 1,
                color: UI.textDim,
              }}>{p.name.toUpperCase()}</span>
              <span style={{ fontFamily: UI.fontNum, fontSize: 9, color: UI.brassBright }}>
                {cost}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Objectives (trial tables) ─────────────────────────────────
export function ObjectivePanel({ snap }) {
  if (!snap.objectives?.length) return null;
  return (
    <div style={{
      background: PANEL, clipPath: facet(8), padding: "10px 14px",
      boxShadow: RAISED,
    }}>
      {snap.objectives.map((o) => (
        <div key={o.id} style={{ marginBottom: 6 }}>
          <div style={{
            display: "flex", justifyContent: "space-between",
            fontFamily: UI.font, fontSize: 10, letterSpacing: 1,
            color: o.done ? UI.good : UI.textDim, marginBottom: 3,
          }}>
            <span>{o.done ? "✓ " : ""}{o.label}</span>
            <span style={{ fontFamily: UI.fontNum }}>
              {o.kind === "score"
                ? `${snap.score.toLocaleString()}/${o.need.toLocaleString()}`
                : `${o.progress}/${o.need}`}
            </span>
          </div>
          <div style={{
            height: 4, background: RECESS, boxShadow: SUNK, overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: `${Math.min(100, (o.progress / o.need) * 100)}%`,
              background: o.done
                ? `linear-gradient(90deg, ${UI.good}, ${UI.brassBright})`
                : `linear-gradient(90deg, ${UI.coolDim}, ${UI.cool})`,
              transition: "width 220ms ease-out",
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Event ticker ──────────────────────────────────────────────
// `fill` gives the ticker whatever height its parent has left over
// (the solo rail's feed). Default is the 92px box the duel console
// has always had. One component, two homes, no second copy.
export function Ticker({ snap, fill = false, size = 11 }) {
  const KIND_COLOR = {
    jackpot: UI.brassBright, spire: UI.brassBright,
    sabotage: UI.hot, tilt: UI.bad, warn: UI.hot,
    save: UI.good, clear: UI.good, node: UI.cool,
    power: UI.cool, mode: UI.brassBright, spite: UI.hot,
    over: UI.bad, drain: UI.textFaint, lost: UI.textFaint,
  };
  return (
    <div style={{
      ...(fill ? { flex: 1, minHeight: 0 } : { height: 92 }),
      overflow: "hidden", display: "flex",
      flexDirection: "column", gap: fill ? 5 : 2, justifyContent: "flex-start",
    }}>
      {snap.log.map((l, i) => (
        <div key={l.key} style={{
          fontFamily: UI.font, fontSize: size, letterSpacing: 0.5,
          color: KIND_COLOR[l.kind] || UI.textDim,
          opacity: 1 - i * 0.18,
          textShadow: SET,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          // The newest line ARRIVES. It doesn't just appear.
          animation: i === 0 ? "tilt-pop-in 260ms ease-out" : "none",
        }}>
          {l.msg}
        </div>
      ))}
    </div>
  );
}

// ── Plunger ───────────────────────────────────────────────────
export function Plunger({ snap, onDown, onUp }) {
  if (snap.phase !== "ready") return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        flex: 1, height: 14, background: RECESS, boxShadow: SUNK,
        clipPath: facetTB(4), overflow: "hidden", position: "relative",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          width: `${snap.plunger * 100}%`,
          background: `linear-gradient(90deg, ${UI.brassDim}, ${UI.brassBright})`,
        }} />
      </div>
      <button
        onMouseDown={onDown} onMouseUp={onUp}
        onTouchStart={(e) => { e.preventDefault(); onDown(); }}
        onTouchEnd={(e) => { e.preventDefault(); onUp(); }}
        style={{
          padding: "10px 22px", clipPath: facetTB(6),
          background: BRASS_METAL, border: "none",
          fontFamily: UI.fontD, fontSize: 12, fontWeight: 700,
          letterSpacing: 2, color: "#1a1206", cursor: "pointer",
          boxShadow: RAISED, textShadow: "0 1px 0 rgba(255,255,255,0.3)",
        }}
      >
        LAUNCH
      </button>
    </div>
  );
}

// ── Big center banner (tilt / cleared / over) ─────────────────
export function Banner({ snap, onRestart, onExit }) {
  const P = snap.phase;
  if (P !== "over" && P !== "cleared" && P !== "tilted") return null;

  const cfg = {
    over:    { text: "GAME OVER",     tint: UI.bad,  sub: `Final: ${snap.score.toLocaleString()}` },
    cleared: { text: "TABLE CLEARED", tint: UI.good, sub: `Score: ${snap.score.toLocaleString()}` },
    tilted:  { text: "TILT",          tint: UI.hot,  sub: "Flippers dead. Watch it drain." },
  }[P];

  return (
    <div style={{
      position: "absolute", inset: 0, display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "rgba(4,5,10,0.82)", backdropFilter: "blur(3px)",
      zIndex: 40, gap: 14,
      animation: P === "tilted" ? "tilt-shake 0.4s ease-in-out" : "none",
    }}>
      <div style={{
        fontFamily: UI.fontD, fontSize: 42, fontWeight: 900, letterSpacing: 8,
        color: cfg.tint, textShadow: glow(cfg.tint),
        animation: "tilt-flicker 3s linear infinite",
      }}>
        {cfg.text}
      </div>
      <div style={{ fontFamily: UI.fontNum, fontSize: 15, color: UI.textDim }}>
        {cfg.sub}
      </div>
      {P !== "tilted" && (
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          {onRestart && (
            <button onClick={onRestart} style={btnStyle(UI.cool)}>PLAY AGAIN</button>
          )}
          {onExit && (
            <button onClick={onExit} style={btnStyle(UI.chromeDim)}>EXIT</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Boss bar — the being's vitals ─────────────────────────────
// Reads snap.boss (null on bossless tables → renders nothing).
// ANIMA is the big bar (its life, the campaign). LYF is the pip
// row (its guard, re-formed every drain — the Lyf Cycle made
// visible). FRACTURED flashes: that flash IS the burst window,
// and the whole reason to be watching this panel.
export function BossBar({ snap, label = "THE KEEPER" }) {
  const B = snap.boss;
  if (!B) return null;
  const pct = (B.anima / B.animaMax) * 100;
  return (
    <div style={{
      background: PANEL, clipPath: facet(8), padding: "10px 12px",
      boxShadow: RAISED, display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
      }}>
        <span style={{
          fontFamily: UI.fontD, fontSize: 11, fontWeight: 900, letterSpacing: 2,
          color: B.felled ? UI.good : UI.hot,
          textShadow: glow(B.felled ? UI.good : UI.hot),
        }}>
          {B.name}
        </span>
        <span style={{ fontFamily: UI.font, fontSize: 8, letterSpacing: 1, color: UI.textFaint }}>
          {label}
        </span>
      </div>

      {/* ANIMA — its life */}
      <div>
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontFamily: UI.font, fontSize: 8, letterSpacing: 2,
          color: UI.textFaint, marginBottom: 3,
        }}>
          <span>ANIMA</span>
          <span style={{ fontFamily: UI.fontNum, color: UI.hot }}>
            {B.anima}/{B.animaMax}
          </span>
        </div>
        <div style={{
          height: 9, background: RECESS, boxShadow: SUNK,
          clipPath: facetTB(3), overflow: "hidden", position: "relative",
        }}>
          <div style={{
            position: "absolute", inset: 0, width: `${pct}%`,
            background: `linear-gradient(90deg, ${UI.hotDim}, ${UI.hot})`,
            transition: "width 160ms ease-out",
          }} />
        </div>
      </div>

      {/* LYF — its guard. Pips, because a guard is COUNTED down. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: UI.font, fontSize: 8, letterSpacing: 2, color: UI.textFaint }}>
          LYF
        </span>
        <div style={{ display: "flex", gap: 3 }}>
          {Array.from({ length: B.lyfMax }).map((_, i) => (
            <div key={i} style={{
              width: 10, height: 7, clipPath: facetTB(2),
              background: i < B.lyf ? UI.cool : UI.panelEdge,
              boxShadow: i < B.lyf ? bloom(UI.cool) : "none",
            }} />
          ))}
        </div>
        {B.fractured && !B.felled && (
          <span style={{
            fontFamily: UI.font, fontSize: 9, letterSpacing: 1,
            color: UI.brassBright, textShadow: glow(UI.brassBright),
            animation: "tilt-danger 0.45s ease-in-out infinite",
          }}>
            ⛓ FRACTURED — STRIKE
          </span>
        )}
        {B.felled && (
          <span style={{
            fontFamily: UI.font, fontSize: 9, letterSpacing: 1,
            color: UI.good, textShadow: glow(UI.good),
          }}>
            ☠ FELLED
          </span>
        )}
      </div>
    </div>
  );
}

// ── Monster bar — the duel's fourth hunger ────────────────────
// YOUR monster lives on THEIR table (it is your avatar; they are
// trying to fell it). This rail spends YOUR charge to make it
// act — attack them as the monster, or BULWARK/MEND it back from
// the brink. Hot palette: everything here crosses the wire.
//
//   duelKit  — the table's boss.duelKit move ids (attacks)
//   oppBoss  — oppSnap.boss (your monster's vitals, or null)
//   onMove   — (id, cost) => void; the screen pays and sends
export function MonsterBar({ snap, oppBoss, duelKit = [], onMove }) {
  if (!oppBoss) return null;
  const attacks = duelKit.map((id) => BOSS_MOVES[id]).filter(Boolean);
  const rail = [
    ...attacks.map((m) => ({ ...m, kind: "attack" })),
    { ...GUARDIAN_MOVES.bulwark, kind: "guard" },
    { ...GUARDIAN_MOVES.mend,    kind: "guard" },
  ];

  const dead = oppBoss.felled;

  return (
    <div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        marginBottom: 6,
      }}>
        <span style={{
          fontFamily: UI.font, fontSize: 9, letterSpacing: 3,
          color: UI.hot, textShadow: glow(UI.hot),
        }}>
          👹 YOUR MONSTER
        </span>
        <span style={{ fontFamily: UI.fontNum, fontSize: 9, color: dead ? UI.bad : UI.textDim }}>
          {dead ? "FELLED" : `${oppBoss.anima}/${oppBoss.animaMax} ♥ · ${oppBoss.lyf}/${oppBoss.lyfMax} ⛨`}
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {rail.map((m) => {
          // A guardian verb is wasted on a full meter; an attack is
          // wasted on a felled monster. Disable the waste — the
          // sender's charge is too precious to eat on a no-op.
          const useless =
            dead ||
            (m.id === "bulwark" && oppBoss.lyf >= oppBoss.lyfMax && !oppBoss.fractured) ||
            (m.id === "mend" && oppBoss.anima >= oppBoss.animaMax);
          const afford = snap.charge >= m.cost && snap.phase === "live" && !useless;
          const tint = m.kind === "guard" ? UI.good : UI.hot;
          return (
            <button
              key={m.id}
              onClick={() => { if (afford) onMove?.(m.id, m.cost); }}
              disabled={!afford}
              title={`${m.name} — ${m.blurb}`}
              style={{
                width: 54, height: 54, clipPath: facet(7),
                background: PANEL, border: "none",
                cursor: afford ? "pointer" : "not-allowed",
                opacity: afford ? 1 : 0.35,
                boxShadow: RAISED,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                padding: 0,
              }}
            >
              <span style={{ fontSize: 17, color: tint, textShadow: glow(tint) }}>
                {m.glyph}
              </span>
              <span style={{
                fontFamily: UI.font, fontSize: 7, letterSpacing: 1, color: UI.textDim,
              }}>
                {m.name}
              </span>
              <span style={{ fontFamily: UI.fontNum, fontSize: 9, color: UI.brassBright }}>
                {m.cost}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const btnStyle = (accent) => ({
  padding: "11px 26px", clipPath: facetTB(7),
  background: PANEL_LIT, border: "none",
  fontFamily: UI.fontD, fontSize: 12, fontWeight: 700, letterSpacing: 2,
  color: accent, cursor: "pointer",
  boxShadow: `${RAISED}, inset 0 0 0 1px ${accent}44`,
  textShadow: glow(accent),
});
